import { useApi, useLoad } from "@allergy123/api-client/react";
import { Button, Card, Select, Status, Table, Td, Th } from "@allergy123/ui";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { errorText } from "./catalog/common";

interface Choice {
  trayPanelId: string;
  allergenProfileId: string;
}

/** Keyed by id, so a different organization never shows the last one's unsaved choices or messages. */
export function OrganizationLocationsPage() {
  const { id = "" } = useParams();
  return <OrganizationLocationsEditor key={id} id={id} />;
}

/**
 * Each location's tray panel and allergen profile. Defaults by state or ZIP
 * prefix apply once locations carry addresses (sub-project 3); until then staff
 * choose here.
 */
function OrganizationLocationsEditor({ id }: { id: string }) {
  const api = useApi();
  const locations = useLoad((a) => a.admin.catalog.organizationLocations(id));
  const organizations = useLoad((a) => a.admin.organizations());
  const panels = useLoad((a) => a.admin.catalog.panels());
  const profiles = useLoad((a) => a.admin.catalog.profiles());
  // Only locations the person has changed; the rest read straight from the loaded list.
  const [choices, setChoices] = useState<Record<string, Choice>>({});
  const [msg, setMsg] = useState<{ kind: "info" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const organization = organizations.data?.organizations.find((o) => o.id === id);

  // Once a reload shows a location with the choices that were saved, it no longer needs its own entry.
  useEffect(() => {
    setChoices((cs) => {
      const next = { ...cs };
      for (const l of locations.data?.locations ?? []) {
        const c = next[l.id];
        if (c && c.trayPanelId === (l.trayPanelId ?? "") && c.allergenProfileId === (l.allergenProfileId ?? "")) delete next[l.id];
      }
      return next;
    });
  }, [locations.data]);

  async function save(locationId: string, locationName: string) {
    const c = choices[locationId];
    if (busy || !c) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.admin.catalog.setLocationCatalog(locationId, { trayPanelId: c.trayPanelId || null, allergenProfileId: c.allergenProfileId || null });
      setMsg({ kind: "info", text: `Saved ${locationName}.` });
      locations.reload();
    } catch (err) {
      setMsg({ kind: "error", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }
  const choose = (loading: boolean) => [{ value: "", label: loading ? "Loading…" : "Not set" }];
  const panelOptions = [...choose(panels.state === "loading"), ...(panels.data?.families ?? []).map((f) => ({ value: f.id, label: `${f.code} · ${f.name}` }))];
  const profileOptions = [...choose(profiles.state === "loading"), ...(profiles.data?.families ?? []).map((f) => ({ value: f.id, label: `${f.code} · ${f.name}` }))];
  return (
    <>
      <div className="mb-8">
        <nav aria-label="Breadcrumb" className="mb-4 text-small">
          <Link to="/organizations" className="text-anchor underline">Organizations</Link>
          {organization && <span className="text-ink-secondary"> / {organization.name}</span>}
        </nav>
        <h1 className="mb-2 text-h1 font-semibold text-anchor">Locations</h1>
        <p className="max-w-[68ch] text-ink-secondary">Which tray panel each location tests with and which allergen profile the testing algorithm reads. The published version in force is recorded when it is used.</p>
      </div>
      {locations.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load locations (${errorText(locations.error)})`} /></div>}
      {panels.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load panels (${errorText(panels.error)})`} /></div>}
      {profiles.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load profiles (${errorText(profiles.error)})`} /></div>}
      {organizations.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load the organization's name (${errorText(organizations.error)})`} /></div>}
      {msg && <div className="mb-6"><Status kind={msg.kind} label={msg.text} /></div>}
      <Card>
        <Table caption={locations.data ? `${locations.data.locations.length} location${locations.data.locations.length === 1 ? "" : "s"}` : locations.state === "error" ? "Not loaded" : "Loading…"}>
          <thead>
            <tr>
              <Th>Location</Th>
              <Th>Tray panel</Th>
              <Th>Allergen profile</Th>
              <Th><span className="sr-only">Actions</span></Th>
            </tr>
          </thead>
          <tbody>
            {locations.data?.locations.map((l) => {
              const c = choices[l.id] ?? { trayPanelId: l.trayPanelId ?? "", allergenProfileId: l.allergenProfileId ?? "" };
              const changed = c.trayPanelId !== (l.trayPanelId ?? "") || c.allergenProfileId !== (l.allergenProfileId ?? "");
              return (
                <tr key={l.id}>
                  <Td className="font-medium">{l.name}</Td>
                  <Td><Select label={`Tray panel for ${l.name}`} value={c.trayPanelId} onChange={(e) => setChoices({ ...choices, [l.id]: { ...c, trayPanelId: e.target.value } })} options={panelOptions} /></Td>
                  <Td><Select label={`Allergen profile for ${l.name}`} value={c.allergenProfileId} onChange={(e) => setChoices({ ...choices, [l.id]: { ...c, allergenProfileId: e.target.value } })} options={profileOptions} /></Td>
                  <Td><Button variant="secondary" surface="card" disabled={busy || !changed} aria-label={`Save ${l.name}`} onClick={() => void save(l.id, l.name)}>Save</Button></Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        {locations.data?.locations.length === 0 && <p className="mt-3 text-ink-secondary">This organization has no locations yet.</p>}
      </Card>
    </>
  );
}
